// src/pages/PlayPage.js
import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAllGames } from "../hooks/useAllGames";
import { useViewport } from "../hooks/useViewport";

export default function PlayPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const { games, loading } = useAllGames();
  const { width, height } = useViewport();

  const isLandscape = width > height;
  const useVerticalAd = isLandscape && width < 1000 && height < 750;

  const viewportWidth = width;
  const viewportHeight = height;

  // 👇 NEW: hide title bar on all mobile screens (portrait & landscape)
  const showTitleBar = viewportHeight >= 500;

  const game = games.find((g) => String(g.id) === String(id));

  if (!game) {
    if (loading) {
      return (
        <div className="grid gap-4">
          <p className="text-sm text-slate-300">Loading game…</p>
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }

  const isKepithor = (game.source || "").toLowerCase() === "kepithor";

  // Kepithor requires login
  if (isKepithor && !user) {
    return (
      <Navigate
        to="/login"
        replace={false}
        state={{ from: { pathname: `/play/${game.id}` } }}
      />
    );
  }

  // ============ Build URL params ============
  const baseUrl = game.webglPath;
  if (!baseUrl) {
    return (
      <div className="grid gap-4 max-w-none w-full">

        {/* TITLE BAR (hidden on mobile) */}
        {showTitleBar && (
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{game.title}</h1>
              <p className="text-sm text-slate-300">
                {isKepithor
                  ? "A Kepithor Studios game, playable in your browser."
                  : `${game.source || "Partner"} game, played in an embedded window.`}
              </p>
            </div>

            <Link
              to="/playable"
              className="px-3 py-2 rounded-xl border border-slate-700 text-sm text-slate-200 hover:border-emerald-400 hover:text-emerald-200 transition"
            >
              ← Back to directory
            </Link>
          </div>
        )}

      </div>
    );
  }

  let url = baseUrl;
  if (isKepithor && user) {
    const params = new URLSearchParams();
    if (user.id) params.set("uid", String(user.id));
    if (user.session) params.set("session", String(user.session));
    if (user.hash) params.set("hash", String(user.hash));
    if (user.displayname) params.set("name", String(user.displayname));

    const qs = params.toString();
    url = qs ? `${baseUrl}?${qs}` : baseUrl;
  }

  // ============ ORIENTATION / SIZING ============
  const isPortrait = game.orientation === "portrait";

  // ============================
  //   LANDSCAPE GAME + VERTICAL AD LAYOUT
  // ============================
  if (useVerticalAd && !isPortrait) {
    const RESERVED_VERTICAL_SMALL = 130;
    const availableHeight = Math.max(viewportHeight - RESERVED_VERTICAL_SMALL, 200);

    let adHeight = Math.min(600, availableHeight);
    let adWidth = adHeight / 3.75;

    let gameHeight = adHeight;
    let gameWidth = (gameHeight * 16) / 9;

    let totalWidth = adWidth + gameWidth + 24;
    if (totalWidth > viewportWidth) {
      const scale = viewportWidth / totalWidth;
      adWidth *= scale;
      adHeight *= scale;
      gameWidth *= scale;
      gameHeight *= scale;
    }

    return (
      <div className="grid gap-4 max-w-none w-full">

        {/* MOBILE floating back button */}
        {!showTitleBar && (
          <Link
            to="/playable"
            className="fixed top-20 left-4 z-50 
                       px-3 py-1 rounded-lg bg-slate-800/80 
                       border border-slate-700 text-xs text-slate-200 
                       backdrop-blur hover:border-emerald-400 transition"
          >
            ← Back
          </Link>
        )}

        {/* TITLE BAR (hidden on mobile) */}
        {showTitleBar && (
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{game.title}</h1>
              <p className="text-sm text-slate-300">
                {isKepithor
                  ? "A Kepithor Studios game, playable in your browser."
                  : `${game.source || "Partner"} game, played in an embedded window.`}
              </p>
            </div>

            <Link
              to="/playable"
              className="px-3 py-2 rounded-xl border border-slate-700 text-sm text-slate-200 hover:border-emerald-400 hover:text-emerald-200 transition"
            >
              ← Back to directory
            </Link>
          </div>
        )}

        {/* GAME + VERTICAL AD */}
        <div className="flex justify-center items-center gap-4 w-full max-w-none">
          <div
            className="rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 shadow-xl shadow-emerald-500/20"
            style={{ width: `${gameWidth}px`, height: `${gameHeight}px` }}
          >
            <iframe title={game.title} src={url} allowFullScreen className="w-full h-full" style={{ border: "none" }} />
          </div>

          <div
            className="rounded-xl overflow-hidden border border-slate-800 bg-slate-900 flex items-center justify-center"
            style={{ width: `${adWidth}px`, height: `${adHeight}px` }}
          >
            <span className="text-[10px] text-slate-400 text-center">
              160×600 Ad<br />
              {Math.round(adWidth)}×{Math.round(adHeight)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ============================
  //   NORMAL PORTRAIT / LANDSCAPE
  // ============================

  const RESERVED_VERTICAL = 200;
  let frameWidth, frameHeight;

  if (isPortrait) {
    const baseMaxWidth = 520;
    const maxW = viewportWidth - 32;
    const maxH = viewportHeight - RESERVED_VERTICAL;
    const wFromH = maxH * (2 / 3);

    frameWidth = Math.max(240, Math.min(baseMaxWidth, maxW, wFromH));
    frameHeight = frameWidth * 1.5;
    
    frameWidth *= 0.95;
    frameHeight *= 0.95;

    // if (viewportWidth < 500) {
    //   frameWidth *= 0.9;
    //   frameHeight *= 0.9;
    // } else if (viewportWidth < 800) {
    //   frameWidth *= 0.88;
    //   frameHeight *= 0.88;
    // } else if (viewportWidth > 800) {
    //   frameWidth *= 1.1;
    //   frameHeight *= 1.1;
    // } else if (viewportWidth > 1000) {
    //   frameWidth *= 1.4;
    //   frameHeight *= 1.4;
    // }

  } else {
    const baseMaxWidth = 1120;
    const maxW = viewportWidth - 32;
    const maxH = viewportHeight - RESERVED_VERTICAL;
    const wFromH = maxH * (16 / 9);

    frameWidth = Math.max(260, Math.min(baseMaxWidth, maxW, wFromH));
    frameHeight = (frameWidth * 9) / 16;

    if (viewportWidth < 1200 && viewportHeight < 900) {
      frameWidth *= 0.82;
      frameHeight *= 0.82;
    }
  }

  return (
    <div className="grid gap-4 max-w-none w-full">

      {/* MOBILE floating back button */}
      {/* {!showTitleBar && (
        <Link
          to="/playable"
          className="fixed top-20 left-4 z-50 
                     px-3 py-1 rounded-lg bg-slate-800/80 
                     border border-slate-700 text-xs text-slate-200 
                     backdrop-blur hover:border-emerald-400 transition"
        >
          ← Back
        </Link>
      )} */}

      {/* TITLE BAR (hidden on mobile) */}
      {/* {showTitleBar && (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{game.title}</h1>
            <p className="text-sm text-slate-300">
              {isKepithor
                ? "A Kepithor Studios game, playable in your browser."
                : `${game.source || "Partner"} game, played in an embedded window.`}
            </p>
          </div>

          <Link
            to="/playable"
            className="px-3 py-2 rounded-xl border border-slate-700 text-sm text-slate-200 hover:border-emerald-400 hover:text-emerald-200 transition"
          >
            ← Back to directory
          </Link>
        </div>
      )} */}

      {/* NORMAL GAME FRAME */}


        <div className="spacer" style={{height: 5}}></div>

      <div
        className="rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 shadow-xl shadow-emerald-500/20 mx-auto flex items-center justify-center"
        style={{ width: `${frameWidth}px`, height: `${frameHeight}px` }}
      >
        <iframe title={game.title} src={url} allowFullScreen className="w-full h-full" style={{ border: "none" }} />
      </div>


      
        {/* <div className="spacer" style={{height: 100}}></div> */}
    </div>
  );
}
